import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatLocalDate } from "@/utils/dateUtils";
import type { TimelineData } from "@/hooks/useDisponibilidade";
import type {
  AtorKind,
  ConfiancaEvento,
  DiffCampo,
  TimelineEvento,
  TimelineLaneKey,
} from "@/types/timeline";

// A RPC get_freelancer_full_timeline (sql/146) ainda não está no types.ts gerado -> cast,
// mesmo padrão do useDisponibilidade.
const timelineDb = supabase as unknown as {
  rpc: (functionName: string, args: Record<string, unknown>) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export type PeriodoTimeline = "12m" | "24m" | "tudo";

// Reexport: o shape do evento é compartilhado com a timeline da agência
// (@/types/timeline). Mantido aqui também para não quebrar os imports
// existentes que apontam para este hook.
export type { AtorKind, ConfiancaEvento, DiffCampo, TimelineEvento, TimelineLaneKey };

export interface GoldenFunil {
  vezes_selecionado: number | null;
  vezes_shortlistado: number | null;
  vezes_aprovado: number | null;
  vezes_preterido: number | null;
  vezes_reprovado: number | null;
  shortlist_efetivo: number | null;
  taxa_conversao_pessoal: number | null;
  ultima_vaga_em: string | null;
}

export interface GoldenFrescor {
  ultima_atualizacao_jotform: string | null;
  ultima_atualizacao_interno: string | null;
  ultima_atualizacao_linkedin: string | null;
  ultima_atualizacao_ia: string | null;
  ultima_atualizacao_geral: string | null;
  atualizacoes_12m: number | null;
  ultima_campanha_enviada_em: string | null;
  ultima_atualizacao_via_campanha_em: string | null;
  respondeu_ultima_campanha: boolean | null;
}

export interface GoldenEngajamento {
  ultimo_contato_em: string | null;
  campanhas_enviadas: number | null;
  campanhas_clicadas: number | null;
  campanhas_respondidas: number | null;
  taxa_clique: number | null;
  taxa_resposta: number | null;
  ultima_atualizacao_via_campanha_em: string | null;
  ultimo_termometro: string | null;
  ultimo_termometro_em: string | null;
}

/** Linha de v_freelancer_capacity (carga/capacidade atual), quando existir. */
export type CapacityEstado = Record<string, unknown> | null;

export interface TimelineCompleta {
  eventos: TimelineEvento[];
  lanes_estado: {
    /** Mesmo shape do get_freelancer_timeline que a DisponibilidadeTimeline consome. */
    disponibilidade: TimelineData | null;
    capacity: CapacityEstado;
  } | null;
  golden: {
    funil: GoldenFunil | null;
    frescor: GoldenFrescor | null;
    engajamento: GoldenEngajamento | null;
  } | null;
}

/** "tudo" = janela larga o suficiente pra cobrir qualquer histórico da plataforma. */
const INICIO_PLATAFORMA = "2015-01-01";

function periodoParaDataInicio(periodo: PeriodoTimeline): string | null {
  if (periodo === "tudo") return INICIO_PLATAFORMA;
  if (periodo === "12m") {
    const inicio = new Date();
    inicio.setMonth(inicio.getMonth() - 12);
    return formatLocalDate(inicio) ?? null;
  }
  // 24m é o default da RPC (p_from null).
  return null;
}

/**
 * Linha do tempo completa de UM freelancer (eventos + estado de disponibilidade +
 * resumos de funil/frescor/engajamento) via RPC get_freelancer_full_timeline (sql/146).
 * queryKey com shape próprio: nunca reusar com SELECT/resposta diferente.
 */
export function useFreelancerTimelineCompleta(
  freelancerId?: string | null,
  periodo: PeriodoTimeline = "24m",
) {
  return useQuery({
    queryKey: ["freelancer-timeline", freelancerId, periodo],
    enabled: !!freelancerId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TimelineCompleta | null> => {
      const rpcArgs: Record<string, unknown> = { p_freelancer_id: freelancerId };
      const dataInicio = periodoParaDataInicio(periodo);
      if (dataInicio) rpcArgs.p_from = dataInicio;
      const { data, error } = await timelineDb.rpc("get_freelancer_full_timeline", rpcArgs);
      if (error) throw error;
      return (data as TimelineCompleta) ?? null;
    },
  });
}
